(() => {
  class MoonreedArrivals extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const on = (target, name, handler, extra = {}) => target.addEventListener(name, handler, {signal:this.abort.signal, ...extra});
      this.track = this.querySelector('[data-arrivals-track]');
      if (!this.track) return;
      this.controls = this.querySelector('[data-arrivals-controls]');
      this.toggle = this.querySelector('[data-arrivals-pause]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true';
      this.paused = Boolean(window.Shopify?.designMode);
      this.hovering = false; this.focused = false; this.visible = false;
      this.speed = Math.min(40, Math.max(10, Number(this.dataset.speed) || 18));
      on(this.toggle, 'click', () => { this.paused = !this.paused; this.sync(); });
      on(this, 'pointerenter', e => { if (e.pointerType === 'mouse') { this.hovering = true; this.sync(); } });
      on(this, 'pointerleave', () => { this.hovering = false; this.sync(); });
      on(this.track, 'focusin', () => { this.focused = true; this.sync(); });
      on(this.track, 'focusout', () => queueMicrotask(() => { this.focused = this.track.contains(document.activeElement); this.sync(); }));
      const pause = () => { this.paused = true; this.sync(); };
      on(this.track, 'pointerdown', pause, {passive:true});
      on(this.track, 'wheel', pause, {passive:true});
      on(this.track, 'keydown', pause);
      on(document, 'visibilitychange', () => this.sync());
      on(this.motion, 'change', () => { this.builtWidth = undefined; this.buildLoop(); this.sync(); });
      [-1, 1].forEach(direction => on(this.querySelector(direction < 0 ? '[data-arrivals-prev]' : '[data-arrivals-next]'), 'click', () => {
        pause();
        this.track.scrollBy({left: direction * this.step(), behavior:this.motion.matches ? 'auto' : 'smooth'});
      }));
      on(this.track, 'scroll', () => this.arrows(), {passive:true});
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.sync(); }, {threshold:.1});
      this.observer.observe(this);
      this.resize = new ResizeObserver(() => { this.buildLoop(); this.sync(); }); this.resize.observe(this.track);
      this.buildLoop();
      this.sync();
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); this.resize?.disconnect(); cancelAnimationFrame(this.frame); this.track?.querySelectorAll('[data-mr-clone]').forEach(card => card.remove()); this.loopWidth = 0; this.builtWidth = undefined; }
    buildLoop() {
      const width = this.track.clientWidth;
      if (this.builtWidth === width) return;
      this.builtWidth = width;
      this.track.querySelectorAll('[data-mr-clone]').forEach(card => card.remove());
      const originals = Array.from(this.track.children);
      this.loopWidth = 0;
      if (!this.autoplay || this.motion.matches || originals.length < 2 || width === 0) return;
      // Decorative repeat cards are mouse/touch shoppable; keyboard and screen-reader
      // users get one canonical set. No forms, duplicate IDs or app widgets are copied.
      const addSet = () => originals.forEach(original => {
        const card = document.createElement('div');
        card.className = original.className;
        card.dataset.mrClone = 'true';
        card.setAttribute('aria-hidden', 'true');
        card.innerHTML = original.innerHTML;
        card.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        card.querySelectorAll('script,form').forEach(node => node.remove());
        card.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(node => node.setAttribute('tabindex', '-1'));
        this.track.append(card);
      });
      addSet();
      this.loopWidth = this.track.children[originals.length].offsetLeft - originals[0].offsetLeft;
      // Short collections still fill the viewport at the seam.
      if (this.loopWidth > 0) {
        const extra = Math.ceil(width / this.loopWidth);
        for (let i = 1; i < extra; i++) addSet();
      }
    }
    step() {
      const [first, second] = this.track.children;
      return second ? second.offsetLeft - first.offsetLeft : first?.getBoundingClientRect().width || 0;
    }
    arrows() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.querySelector('[data-arrivals-prev]').disabled = this.track.scrollLeft <= 1;
      this.querySelector('[data-arrivals-next]').disabled = this.track.scrollLeft >= max - 1;
    }
    sync() {
      cancelAnimationFrame(this.frame);
      if (!this.isConnected) return;
      const overflow = this.track.scrollWidth > this.track.clientWidth + 2;
      this.controls.hidden = !overflow;
      this.toggle.hidden = !this.autoplay || this.motion.matches;
      this.toggle.textContent = this.paused ? this.dataset.play : this.dataset.pause;
      this.toggle.setAttribute('aria-pressed', String(this.paused));
      this.arrows();
      // Repeated visual cards make a short collection loop without jumping backwards.
      const canLoop = this.loopWidth > 0;
      if (!this.autoplay || this.paused || this.motion.matches || !this.visible || this.hovering || this.focused || document.hidden || !canLoop) return;
      this.position = this.track.scrollLeft % this.loopWidth;
      this.last = null;
      const tick = time => {
        const elapsed = this.last === null ? 0 : Math.min(50, time - this.last);
        this.last = time;
        this.position += elapsed / 1000 * this.speed;
        if (this.position >= this.loopWidth) this.position -= this.loopWidth;
        this.track.scrollLeft = this.position;
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
  }
  if (!customElements.get('mr-arrivals-strip')) customElements.define('mr-arrivals-strip', MoonreedArrivals);
})();
